import { Button } from '@jouzen/ecom-components/v3/Button';
import { GridItem } from '@jouzen/ecom-components/v3/GridItem';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { Accordion, AccordionItem } from '@jouzen/ecom-components/v4/Accordion';
import Link from 'next/link';
import { useLocale, useTranslations } from 'next-intl';

import { EventType } from '@/analytics/types';
import Motion from '@/app/components/Motion';
import RichText from '@/app/components/RichText';
import SectionHeading from '@/app/components/SectionHeading';
import getLocalePartialUrl from '@/utils/getLocalePartialUrl';

import type { FaqItemData } from '../_types/faq';

export interface FaqSectionProps {
  readonly items: FaqItemData[];
}

const linkTValues = (url: string | undefined, locale: string) => {
  if (url) {
    return {
      link: (chunks: React.ReactNode) => (
        <Link
          href={`${getLocalePartialUrl(locale)}${url}`}
          className="text-primary-500 underline"
          target="_blank"
        >
          {chunks}
        </Link>
      ),
    };
  }
  return undefined;
};

const learnMoreLinkTValues = (url: string | undefined, locale: string) => {
  if (url) {
    return {
      learnMoreLink: (chunks: React.ReactNode) => (
        <Button
          href={`${getLocalePartialUrl(locale)}${url}`}
          variant="secondary-light"
          target="_blank"
        >
          {chunks}
        </Button>
      ),
    };
  }
  return undefined;
};

const onAccordionOpenChange = (newIndex: number | null) => {
  // Only call analytics track when a new section is opened
  if (newIndex !== null) {
    void window.ouraAnalytics.track(EventType.ModuleClicked, {
      action: 'expand section',
      location: 'faqs',
    });
  }
};

const FaqSection = ({ items }: FaqSectionProps) => {
  const t = useTranslations();
  const locale = useLocale();

  return (
    <section className="py-20" data-cy="hardware-pdp-faq">
      <LayoutGrid rowGap={0}>
        <GridItem colStart={{ sm: 'main' }} colEnd={{ sm: 'main' }}>
          <SectionHeading
            animated
            content="ring_pdp_2025_faqs_section_title"
            typographyProps={{
              className:
                'leading-tighter tracking-tighter mb-16 lg:mb-20 xl:mb-24 md:text-center',
              Element: 'h2',
              variant: 'h2',
              weight: 'light',
            }}
          />
        </GridItem>
        <GridItem
          colStart={{ sm: 'main', lg: 6 }}
          colEnd={{ sm: 'main', lg: 20 }}
        >
          <Motion
            initial={{ opacity: 0 }}
            transition={{
              delay: 0.33,
              duration: 0.75,
              type: 'tween',
            }}
            viewport={{ once: true }}
            whileInView={{ opacity: 1 }}
          >
            <Accordion
              className="gap-4"
              defaultOpenIndex={0}
              headerClassName="md:text-xl font-bold pr-4"
              onOpenChange={onAccordionOpenChange}
            >
              {items.map(
                ({ answer, id, learnMoreLink, link, list, question }) => (
                  <AccordionItem header={t(question)} key={id}>
                    <div className="lg:text-base">
                      <RichText
                        messageKey={answer}
                        tValues={{
                          ...linkTValues(link, locale),
                          ...learnMoreLinkTValues(learnMoreLink, locale),
                        }}
                      />
                      {list && (
                        <div className="mt-1 ml-2 space-y-1">
                          <RichText messageKey={list} />
                        </div>
                      )}
                    </div>
                  </AccordionItem>
                ),
              )}
            </Accordion>
          </Motion>
        </GridItem>
      </LayoutGrid>
    </section>
  );
};

export default FaqSection;
